import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA' };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = v => `$${Math.round(v || 0).toLocaleString()}`;
const lrColor = lr => (lr >= 70 ? C.red : lr >= 65 ? C.amber : C.green);

export default function PackPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/finance/public/reinsurer-pack/${token}`)
      .then(r => setData(r.data))
      .catch(() => setError('This pack link is invalid or has expired. Please contact Underaiter for a fresh link.'));
  }, [token]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
      <p className="text-sm max-w-md text-center" style={{ color: C.slate }} data-testid="pack-portal-error">{error}</p>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
      <p className="text-sm" style={{ color: C.slate }}>Loading pack…</p>
    </div>
  );

  const { premium: p, claims: c, ratios: r, basis: b } = data;
  const kpis = [
    { label: 'Written Premium', value: money(p.written_premium) },
    { label: 'Earned Premium', value: money(p.earned_premium) },
    { label: 'Claims Incurred', value: money(c.incurred) },
    { label: 'Incurred LR', value: `${r.incurred_loss_ratio}%`, color: lrColor(r.incurred_loss_ratio) },
    { label: 'Ultimate LR (incl. IBNR)', value: `${r.ultimate_loss_ratio}%` },
    { label: 'Net LR (QS 25%)', value: `${r.net_incurred_loss_ratio}%` },
  ];
  const integ = data.integrity;

  return (
    <div className="min-h-screen" style={{ background: C.bg }} data-testid="pack-portal-page">
      <div style={{ background: '#0F2440' }} className="px-6 py-5">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
          <span className="text-white text-lg font-bold" style={{ fontFamily: 'Georgia, serif' }}>
            under<span style={{ color: '#FF9933' }}>AI</span>ter<span style={{ color: '#4A8ED4', fontWeight: 400 }}>.com</span>
          </span>
          <span className="text-[11px] px-2 py-0.5 rounded-sm font-bold uppercase" style={{ background: 'rgba(255,255,255,0.12)', color: '#CBD8E6' }}>Reinsurer Pack · Read-only</span>
          <span className="ml-auto text-[11px]" style={{ color: '#8FA6BD' }}>As at {data.as_at} · figures audited nightly</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="pack-portal-kpis">
          {kpis.map(k => (
            <div key={k.label} className="bg-white rounded-md p-3" style={{ border: '1px solid rgba(136,150,168,0.15)' }}>
              <p className="text-[9px] uppercase tracking-wider" style={{ color: C.slate }}>{k.label}</p>
              <p className="text-base font-bold font-mono mt-1" style={{ color: k.color || C.navy }}>{k.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-md p-4" style={{ border: '1px solid rgba(136,150,168,0.15)' }}>
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: C.blue }}>Methodology &amp; Basis</h3>
          <p className="text-[11px] leading-relaxed" style={{ color: C.slate }}>
            <strong>Numerator:</strong> {b.numerator} · <strong>Denominator:</strong> {b.denominator} · <strong>Reinsurance:</strong> {b.reinsurance} · <strong>IBNR:</strong> {b.ibnr}
          </p>
        </div>

        <div className="bg-white rounded-md p-4 overflow-x-auto" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="pack-portal-classes">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.blue }}>Loss Ratio &amp; Profitability by Class</h3>
          <table className="w-full text-[11px]">
            <thead>
              <tr style={{ color: C.slate }} className="text-left uppercase text-[9px] tracking-wider">
                <th className="py-1.5 pr-3">Class</th><th className="py-1.5 pr-3 text-right">Policies</th>
                <th className="py-1.5 pr-3 text-right">GWP</th><th className="py-1.5 pr-3 text-right">Earned</th>
                <th className="py-1.5 pr-3 text-right">Claims</th><th className="py-1.5 pr-3 text-right">Incurred</th>
                <th className="py-1.5 pr-3 text-right">Loss Ratio</th><th className="py-1.5 text-right">Combined (pre-expenses)</th>
              </tr>
            </thead>
            <tbody>
              {data.classes.map(cl => (
                <tr key={cl.class} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)', color: C.navy }}>
                  <td className="py-1.5 pr-3 font-medium">{cl.class}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{cl.policies.toLocaleString()}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{money(cl.gwp)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{money(cl.earned_premium)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{cl.claims_count.toLocaleString()}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{money(cl.claims_incurred)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono font-bold" style={{ color: lrColor(cl.loss_ratio) }}>{cl.loss_ratio}%</td>
                  <td className="py-1.5 text-right font-mono" data-testid={`pack-class-combined-${cl.class}`}>{cl.combined_ratio_pre_expenses}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-md p-4 overflow-x-auto" style={{ border: '1px solid rgba(136,150,168,0.15)' }}>
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.blue }}>Incurred by Accident Quarter</h3>
            <table className="w-full text-[11px]">
              <thead><tr style={{ color: C.slate }} className="text-left uppercase text-[9px] tracking-wider">
                <th className="py-1.5 pr-3">Quarter</th><th className="py-1.5 pr-3 text-right">Claims</th>
                <th className="py-1.5 pr-3 text-right">Paid</th><th className="py-1.5 pr-3 text-right">Reserves</th><th className="py-1.5 text-right">Incurred</th>
              </tr></thead>
              <tbody>
                {data.accident_quarters.map(q => (
                  <tr key={q.quarter} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)', color: C.navy }}>
                    <td className="py-1.5 pr-3 font-medium">{q.quarter}</td>
                    <td className="py-1.5 pr-3 text-right font-mono">{q.claims.toLocaleString()}</td>
                    <td className="py-1.5 pr-3 text-right font-mono">{money(q.paid)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono">{money(q.reserves)}</td>
                    <td className="py-1.5 text-right font-mono">{money(q.incurred)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-md p-4" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="pack-portal-integrity">
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.blue }}>Data Integrity Discipline — 30 Days</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Current status', value: integ.currently_clean ? 'CLEAN' : 'OPEN ISSUES', color: integ.currently_clean ? C.green : C.red },
                { label: 'Clean streak', value: `${integ.clean_streak} days` },
                { label: 'Clean days', value: `${integ.clean_days}/${integ.days_audited}` },
                { label: 'Drift auto-repaired', value: integ.total_repaired.toLocaleString() },
              ].map(k => (
                <div key={k.label} className="p-3 rounded-sm" style={{ background: C.bg }}>
                  <p className="text-[9px] uppercase tracking-wider" style={{ color: C.slate }}>{k.label}</p>
                  <p className="text-sm font-bold font-mono mt-0.5" style={{ color: k.color || C.navy }}>{k.value}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] mt-3 leading-relaxed" style={{ color: C.slate }}>
              Every record is audited nightly against 20 insurance-reality rules; drift is auto-repaired by the platform's workflow layer.
            </p>
          </div>
        </div>

        <p className="text-[10px] text-center pb-6" style={{ color: '#64748B' }}>
          Confidential — intended solely for the named capacity provider. Generated {new Date(data.generated_at).toLocaleString()} · Powered by underAIter.com, Evolutionary Insurance Intelligence.
        </p>
      </div>
    </div>
  );
}
